import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
  Table,
  Spinner,
  CustomInput,
  Alert,
  Collapse,
  Progress
} from 'reactstrap';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import { createFullLengthExam } from '../../../redux/QuestionBank/FullLengthExamAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import ResizableImage from '../../../components/QuestionBank/ResizableImage';
import { textToDraftRaw, textToDraftRawWithImage, draftRawToPlainText } from '../../../util/draftToHtml';
import { uploadImage } from '../../../util/imageUpload';
import { optimizeImage } from '../../../util/imageOptimize';
import { resolveSubject, resolveUnit, resolveTopic } from '../../../util/resolveSubjectUnitTopic';
import {
  questionTextToolbar,
  choiceToolbar,
  explanationToolbar,
  editorContainerStyles
} from '../../../util/editorToolbarConfig';

// Dynamic choice support constants
const MAX_CHOICES = 10;
const CHOICE_LABELS = Array.from({ length: MAX_CHOICES }, (_, i) => String.fromCharCode(65 + i));
const CORRECT_ANSWER_MAP = Object.fromEntries(CHOICE_LABELS.map((label, i) => [label, i.toString()]));
const ANSWER_DROPDOWN_STRING = CHOICE_LABELS.join(',');
const FIRST_CHOICE_COL = 5; // 0-indexed column of Choice A (col 3=Passage Title, col 4=Question Text)
const EXPLANATION_COL = FIRST_CHOICE_COL + MAX_CHOICES + 1; // 16
const QUESTION_IMAGE_COL = EXPLANATION_COL + 1; // 17
const QUESTION_IMAGE_WIDTH_COL = QUESTION_IMAGE_COL + 1; // 18
const FIRST_CHOICE_IMAGE_COL = QUESTION_IMAGE_WIDTH_COL + 1; // 19, stride 2: choice i image = +i*2, width = +i*2+1
const EXPLANATION_IMAGE_COL = FIRST_CHOICE_IMAGE_COL + MAX_CHOICES * 2; // 39
const EXPLANATION_IMAGE_WIDTH_COL = EXPLANATION_IMAGE_COL + 1; // 40
const SUPPORTED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

const CreateFullLengthExamPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { createLoading } = useSelector(state => state.fullLengthExams || {});
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);

  // Form state
  const [examName, setExamName] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Section configuration state
  const [enableSections, setEnableSections] = useState(false);
  const [sectionConfig, setSectionConfig] = useState([
    { name: 'Section 1', questionCount: 0, timeLimitMinutes: 180 },
    { name: 'Section 2', questionCount: 0, timeLimitMinutes: 180 },
  ]);
  const [breakDurationMinutes, setBreakDurationMinutes] = useState(60);

  // Upload state
  const [file, setFile] = useState(null);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [parsedPassages, setParsedPassages] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [isParsing, setIsParsing] = useState(false);

  // Image/ZIP state
  const [imageFiles, setImageFiles] = useState({});
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  // Review state
  const [editingIndex, setEditingIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Lazy WYSIWYG editor states
  const [editorStates, setEditorStates] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 25;

  // Fetch subjects, units, topics for template dropdowns
  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
  }, [dispatch]);

  // Helper: sanitize name for Excel named range
  const sanitizeNameForExcel = (name) => {
    return 'R_' + name
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/^[0-9]/, 'N$&')
      .substring(0, 250);
  };

  // Helper: create EditorState from plain text or Draft.js raw object
  const createEditorState = (value) => {
    if (!value) return EditorState.createEmpty();
    if (typeof value === 'object' && value.blocks) {
      try { return EditorState.createWithContent(convertFromRaw(value)); } catch (e) { console.warn('Failed to convert Draft.js content:', e); }
    }
    if (typeof value === 'string') return EditorState.createWithContent(ContentState.createFromText(value));
    return EditorState.createEmpty();
  };

  // Lazily initialize editor states when a question is expanded
  useEffect(() => {
    if (editingIndex !== null && !editorStates[editingIndex]) {
      const question = parsedQuestions[editingIndex];
      if (question) {
        const choiceEditors = {};
        const numChoices = Math.max(question.choices?.length || 0, 2);
        for (let ci = 0; ci < numChoices; ci++) {
          choiceEditors[`choice${CHOICE_LABELS[ci]}`] = createEditorState(
            question.choices?.[ci]?.text || (question._plainChoices ? question._plainChoices[ci] : null)
          );
        }
        setEditorStates(prev => ({
          ...prev,
          [editingIndex]: {
            questionText: createEditorState(question.questionText),
            ...choiceEditors,
            explanation: createEditorState(question.explanationText),
          }
        }));
      }
    }
  }, [editingIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Replace blob: URLs in Draft.js raw content entityMap with Firebase URLs
  const replaceBlobUrl = (raw, blobUrl, firebaseUrl) => {
    if (!raw || !raw.entityMap || !blobUrl || !firebaseUrl) return raw;
    const result = JSON.parse(JSON.stringify(raw));
    Object.values(result.entityMap).forEach(entity => {
      if (entity.type === 'IMAGE' && entity.data?.src === blobUrl) {
        entity.data.src = firebaseUrl;
      }
    });
    return result;
  };

  // Finalize Draft.js raw content for saving: replace blob URL or add Firebase image
  const finalizeRawContent = (content, blobUrl, firebaseUrl, width) => {
    let raw = typeof content === 'object' && content.blocks ? content : textToDraftRaw(content);
    if (blobUrl && firebaseUrl) {
      return replaceBlobUrl(raw, blobUrl, firebaseUrl);
    } else if (!blobUrl && firebaseUrl) {
      return textToDraftRawWithImage(content, firebaseUrl, width);
    }
    return raw;
  };

  // Check if choice at index has content (text or image entity)
  const choiceHasContent = (q, idx) => {
    if (q._plainChoices?.[idx]) return true;
    const choiceRaw = q.choices?.find(c => c.choiceId === idx.toString())?.text;
    return choiceRaw?.entityMap && Object.keys(choiceRaw.entityMap).length > 0;
  };

  // Handle WYSIWYG editor changes for full-length exam questions
  const handleEditorChange = (index, field, editorState) => {
    setEditorStates(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: editorState }
    }));

    const plainText = editorState.getCurrentContent().getPlainText();
    const raw = convertToRaw(editorState.getCurrentContent());

    // Update the question — store both plain text (for display) and raw (for save)
    setParsedQuestions(prev => {
      const updated = [...prev];
      const q = { ...updated[index] };

      if (field === 'questionText') {
        q._plainQuestionText = plainText;
        q.questionText = raw;
      } else if (field.startsWith('choice')) {
        const choiceLetter = field.replace('choice', '');
        const choiceIdx = CHOICE_LABELS.indexOf(choiceLetter);
        if (choiceIdx >= 0) {
          // Update plain text
          if (!q._plainChoices) q._plainChoices = [];
          while (q._plainChoices.length <= choiceIdx) q._plainChoices.push('');
          q._plainChoices[choiceIdx] = plainText;

          const choiceIdStr = choiceIdx.toString();
          if (plainText) {
            const existing = (q.choices || []).find(c => c.choiceId === choiceIdStr);
            if (existing) {
              q.choices = q.choices.map(c => c.choiceId === choiceIdStr ? { ...c, text: raw } : c);
            } else {
              q.choices = [...(q.choices || []), { choiceId: choiceIdStr, text: raw, label: CHOICE_LABELS[choiceIdx] }];
              q.choices.sort((a, b) => a.choiceId.localeCompare(b.choiceId));
            }
          } else if (choiceIdx >= 2) {
            // Only remove optional choices (C and beyond) when empty
            q.choices = (q.choices || []).filter(c => c.choiceId !== choiceIdStr);
          }
        }
      } else if (field === 'explanation') {
        q._plainExplanation = plainText;
        q.explanationText = raw;
      }

      // Re-validate
      const rowErrors = [];
      if (!q.subjectName) rowErrors.push('Subject is required');
      if (!q._plainQuestionText) rowErrors.push('Question Text is required');
      if (!choiceHasContent(q, 0)) rowErrors.push('Choice A is required');
      if (!choiceHasContent(q, 1)) rowErrors.push('Choice B is required');
      if (!q.correctAnswerLabel) {
        rowErrors.push('Correct Answer is required');
      } else if (!CHOICE_LABELS.includes(q.correctAnswerLabel)) {
        rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
      } else {
        const correctIdx = CHOICE_LABELS.indexOf(q.correctAnswerLabel);
        if (!choiceHasContent(q, correctIdx)) rowErrors.push(`Correct Answer is ${q.correctAnswerLabel} but Choice ${q.correctAnswerLabel} is empty`);
      }
      q.hasErrors = rowErrors.length > 0;
      q.errors = rowErrors;

      updated[index] = q;
      return updated;
    });
  };

  // Refs for stable image resize handler (avoids stale closures)
  const editorStatesRef = useRef(editorStates);
  editorStatesRef.current = editorStates;
  const handleEditorChangeRef = useRef(handleEditorChange);
  handleEditorChangeRef.current = handleEditorChange;

  // Block renderer for ResizableImage support in all editors
  const createBlockRenderer = useCallback((questionIndex, field) => (block) => {
    if (block.getType() === 'atomic') {
      return {
        component: ResizableImage,
        editable: false,
        props: {
          onResize: (entityKey, newWidth) => {
            const currentEditorState = editorStatesRef.current[questionIndex]?.[field];
            if (!currentEditorState) return;
            const contentState = currentEditorState.getCurrentContent();
            const newContentState = contentState.mergeEntityData(entityKey, { width: String(newWidth) });
            const newEditorState = EditorState.push(currentEditorState, newContentState, 'apply-entity');
            handleEditorChangeRef.current(questionIndex, field, newEditorState);
          }
        }
      };
    }
    return undefined;
  }, []);

  // Format time
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  // Get plain text from question for display
  const getPlainText = (draftContent) => {
    if (!draftContent) return '';
    if (typeof draftContent === 'string') return draftContent;
    return draftRawToPlainText(draftContent);
  };

  // Extract ZIP file and return Excel file + images map
  const extractZip = async (zipFile) => {
    const zip = await JSZip.loadAsync(zipFile);
    let excelFile = null;
    const images = {};

    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir || path.startsWith('__MACOSX')) continue;
      const filename = path.split('/').pop();
      if (!filename) continue;
      const ext = filename.split('.').pop().toLowerCase();

      if (['xlsx', 'xls', 'csv'].includes(ext) && !excelFile) {
        const blob = await entry.async('blob');
        excelFile = new File([blob], filename, { type: blob.type });
      } else if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) {
        const blob = await entry.async('blob');
        images[filename.toLowerCase()] = new File([blob], filename, { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` });
      }
    }

    if (!excelFile) throw new Error('No Excel file found in ZIP. Please include an .xlsx, .xls, or .csv file.');
    return { excelFile, images };
  };

  // Download Excel template with cascading dropdowns
  const downloadTemplate = async () => {
    if (!subjects || subjects.length === 0) {
      Swal.fire('No Data', 'Please wait for subjects to load before downloading the template.', 'warning');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'PMR Prep';
      workbook.created = new Date();

      // Build subject/unit/topic display data
      const subjectsList = subjects.map(s => ({
        name: s.name,
        displayName: s.name
      })).filter(s => s.name).sort((a, b) => a.name.localeCompare(b.name));
      const subjectDisplayNames = subjectsList.map(s => s.displayName);

      const unitsList = (units || []).map(u => {
        const subject = subjects.find(s => s.subjectId === u.subjectId);
        return {
          name: u.name,
          displayName: u.name,
          subjectDisplayName: subject?.name || ''
        };
      }).filter(u => u.name);

      const topicsList = (topics || []).map(t => {
        const unit = (units || []).find(u => u.unitId === t.unitId);
        return {
          name: t.name,
          displayName: t.name,
          unitDisplayName: unit?.name || ''
        };
      }).filter(t => t.name);

      // Group units by subject and topics by unit
      const unitsBySubject = {};
      unitsList.forEach(u => {
        if (!unitsBySubject[u.subjectDisplayName]) unitsBySubject[u.subjectDisplayName] = [];
        unitsBySubject[u.subjectDisplayName].push(u.displayName);
      });

      const topicsByUnit = {};
      topicsList.forEach(t => {
        if (!topicsByUnit[t.unitDisplayName]) topicsByUnit[t.unitDisplayName] = [];
        topicsByUnit[t.unitDisplayName].push(t.displayName);
      });

      // ===== Hidden Lists sheet for named ranges =====
      const listsSheet = workbook.addWorksheet('Lists');
      listsSheet.state = 'veryHidden';

      let currentCol = 1;
      subjectDisplayNames.forEach((displayName, i) => {
        listsSheet.getCell(i + 1, currentCol).value = displayName;
      });
      workbook.definedNames.add(`Lists!$A$1:$A$${subjectDisplayNames.length}`, 'SubjectsList');

      currentCol = 2;
      Object.keys(unitsBySubject).sort().forEach(subjectDisplayName => {
        const unitsForSubject = unitsBySubject[subjectDisplayName];
        unitsForSubject.forEach((unitDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = unitDisplayName;
        });
        const rangeName = sanitizeNameForExcel(subjectDisplayName);
        const colLetter = String.fromCharCode(64 + currentCol);
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${unitsForSubject.length}`, rangeName);
        currentCol++;
      });

      Object.keys(topicsByUnit).sort().forEach(unitDisplayName => {
        const topicsForUnit = topicsByUnit[unitDisplayName];
        topicsForUnit.forEach((topicDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = topicDisplayName;
        });
        const rangeName = sanitizeNameForExcel('T_' + unitDisplayName);
        let colLetter;
        if (currentCol <= 26) {
          colLetter = String.fromCharCode(64 + currentCol);
        } else {
          const first = Math.floor((currentCol - 1) / 26);
          const second = ((currentCol - 1) % 26) + 1;
          colLetter = String.fromCharCode(64 + first) + String.fromCharCode(64 + second);
        }
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${topicsForUnit.length}`, rangeName);
        currentCol++;
      });

      // ===== Reference Data sheet =====
      const refSheet = workbook.addWorksheet('Reference_Data');
      refSheet.getRow(1).values = ['Subject', 'Unit', 'Topic'];
      refSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      refSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };

      const sortedUnits = [...unitsList].sort((a, b) => a.name.localeCompare(b.name));
      const sortedTopics = [...topicsList].sort((a, b) => a.name.localeCompare(b.name));
      const maxRows = Math.max(subjectsList.length, sortedUnits.length, sortedTopics.length);
      for (let i = 0; i < maxRows; i++) {
        refSheet.getRow(i + 2).values = [
          subjectsList[i]?.name || '',
          sortedUnits[i]?.name || '',
          sortedTopics[i]?.name || ''
        ];
      }
      refSheet.columns = [{ width: 25 }, { width: 30 }, { width: 25 }];

      // ===== Questions Template sheet =====
      const questionsSheet = workbook.addWorksheet('Questions Template');
      const templateColumns = [
        'Subject Name', 'Unit Name', 'Topic Name (Optional)',
        'Passage Title (Optional)',
        'Question Text', ...CHOICE_LABELS.map(l => `Choice ${l}`),
        'Correct Answer', 'Explanation',
        'Question Image (filename)',
        'Question Image Width (px)',
        ...CHOICE_LABELS.flatMap(l => [`Choice ${l} Image (filename)`, `Choice ${l} Image Width (px)`]),
        'Explanation Image (filename)',
        'Explanation Image Width (px)'
      ];
      const imageColStart = templateColumns.indexOf('Question Image (filename)');
      questionsSheet.getRow(1).values = templateColumns;
      questionsSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      questionsSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E72E4' } };

      // Style image columns with orange header
      const tplHeaderRow = questionsSheet.getRow(1);
      for (let ci = imageColStart; ci < templateColumns.length; ci++) {
        const cell = tplHeaderRow.getCell(ci + 1);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFED7D31' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }

      questionsSheet.columns = [
        { width: 22 }, { width: 28 }, { width: 22 },
        { width: 30 },
        { width: 60 }, ...CHOICE_LABELS.map(() => ({ width: 40 })),
        { width: 15 }, { width: 60 },
        { width: 28 }, { width: 18 },  // Question Image + Width
        ...CHOICE_LABELS.flatMap(() => [{ width: 22 }, { width: 18 }]),  // Choice A-J Image + Width
        { width: 28 }, { width: 18 }   // Explanation Image + Width
      ];

      // Pre-fill 20 rows with defaults
      const correctAnswerCellIndex = FIRST_CHOICE_COL + MAX_CHOICES + 1; // 1-indexed cell for Correct Answer
      for (let i = 2; i <= 21; i++) {
        const row = questionsSheet.getRow(i);
        row.getCell(1).value = subjectDisplayNames[0] || '';
        row.getCell(correctAnswerCellIndex).value = 'A';
      }

      // Add cascading dropdown validations for rows 2-201
      for (let rowNum = 2; rowNum <= 201; rowNum++) {
        // Subject dropdown (Column A)
        questionsSheet.getCell(`A${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: ['SubjectsList'],
          showErrorMessage: true,
          errorTitle: 'Invalid Subject',
          error: 'Please select a subject from the dropdown list'
        };

        // Unit dropdown (Column B) - cascading from Subject
        questionsSheet.getCell(`B${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`INDIRECT("R_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(A${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
          showErrorMessage: true,
          errorTitle: 'Invalid Unit',
          error: 'Please select a unit from the dropdown. Make sure to select a Subject first!'
        };

        // Topic dropdown (Column C) - cascading from Unit
        if (Object.keys(topicsByUnit).length > 0) {
          questionsSheet.getCell(`C${rowNum}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`INDIRECT("R_T_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(B${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
            showErrorMessage: true,
            errorTitle: 'Invalid Topic',
            error: 'Please select a topic from the dropdown or leave blank. Make sure to select a Unit first!'
          };
        }

        // Passage Title dropdown (Column D) - references Passages sheet
        questionsSheet.getCell(`D${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['Passages!$A$2:$A$51'],
          showErrorMessage: true,
          errorTitle: 'Invalid Passage',
          error: 'Please select a passage title from the Passages sheet or leave blank.'
        };

        // Correct Answer dropdown
        const correctAnswerCol = String.fromCharCode(65 + FIRST_CHOICE_COL + MAX_CHOICES);
        questionsSheet.getCell(`${correctAnswerCol}${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`"${ANSWER_DROPDOWN_STRING}"`],
          showErrorMessage: true,
          errorTitle: 'Invalid Answer',
          error: `Please select ${ANSWER_DROPDOWN_STRING}`
        };
      }

      // Freeze header row
      questionsSheet.views = [{ state: 'frozen', ySplit: 1 }];

      // ===== Passages sheet =====
      const passagesSheet = workbook.addWorksheet('Passages');
      passagesSheet.getRow(1).values = ['Passage Title', 'Passage Text'];
      passagesSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      passagesSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF28A745' } };
      passagesSheet.columns = [{ width: 30 }, { width: 80 }];
      passagesSheet.views = [{ state: 'frozen', ySplit: 1 }];

      // ===== Instructions sheet =====
      const instructionsSheet = workbook.addWorksheet('Instructions');
      const instructions = [
        ['Full-Length Exam Upload Template'],
        [''],
        ['HOW TO USE THIS TEMPLATE:'],
        [''],
        ['1. Go to the "Questions Template" sheet to enter your questions'],
        ['2. Select a SUBJECT first - this will filter the Unit dropdown!'],
        ['3. Select a UNIT - this will filter the Topic dropdown!'],
        ['4. For reading comprehension, go to the "Passages" sheet first to define your passages'],
        ['5. Back in "Questions Template", select a Passage Title from the dropdown to link a question to a passage'],
        ['6. Fill in the Question, Choices (A and B required, C-J optional), Correct Answer, and Explanation'],
        [''],
        ['CASCADING DROPDOWNS:'],
        ['Subject dropdown shows all available subjects'],
        ['Unit dropdown automatically shows only units for the selected subject'],
        ['Topic dropdown automatically shows only topics for the selected unit'],
        ['Passage Title dropdown shows passages defined in the "Passages" sheet'],
        [''],
        ['PASSAGES (Reading Comprehension):'],
        ['- Go to the "Passages" sheet to enter your passage titles and text'],
        ['- Then in "Questions Template", select a passage title from the dropdown (Column D)'],
        ['- Multiple questions can share the same passage title'],
        [''],
        ['Column Descriptions:'],
        ['- Subject Name: Select from dropdown (required)'],
        ['- Unit Name: Auto-filtered by Subject (required)'],
        ['- Topic Name: Auto-filtered by Unit (optional)'],
        ['- Passage Title: Select from Passages sheet to link this question to a passage (optional)'],
        ['- Question Text: The question stem (required)'],
        ['- Choice A-J: Answer options (A and B required, C-J optional)'],
        [`- Correct Answer: Select ${ANSWER_DROPDOWN_STRING} (required)`],
        ['- Explanation: Explanation shown after answering (recommended)'],
        ['- Question Image / Choice Image / Explanation Image: Filename of an image in the /images folder of the ZIP (optional)'],
        ['- Question Image Width / Choice Image Width / Explanation Image Width: Display width in pixels (optional, defaults: question=250, choices=150, explanation=250)'],
        [''],
        ['IMAGE SUPPORT (Optional):'],
        ['To add images to questions, choices, or explanations:'],
        ['1. Create a ZIP file containing your Excel file and an /images folder'],
        ['2. In the image columns, enter just the filename (e.g., "diagram1.png")'],
        ['3. Place the corresponding image files in the /images folder inside the ZIP'],
        ['4. Optionally set image widths in the width columns (e.g., "200" for 200px)'],
        ['5. Upload the .zip file instead of the .xlsx file'],
        [''],
        ['Supported image formats: .png, .jpg, .jpeg, .gif, .webp, .svg'],
        [''],
        ['NOTE: First 20 rows are pre-filled with defaults. You can add more rows as needed.'],
        [''],
        ['AVAILABLE DATA:'],
        [''],
        ['Subjects:'],
        ...subjectDisplayNames.map(s => [`  - ${s}`]),
        [''],
        ['Units by Subject:'],
        ...Object.keys(unitsBySubject).sort().flatMap(subj => [
          [`  ${subj}:`],
          ...unitsBySubject[subj].map(u => [`      - ${u}`])
        ]),
      ];

      instructions.forEach((row, index) => {
        const excelRow = instructionsSheet.getRow(index + 1);
        excelRow.values = row;
        if (index === 0) {
          excelRow.font = { bold: true, size: 16, color: { argb: 'FF5E72E4' } };
        } else if (row[0]?.startsWith('HOW TO USE') || row[0]?.startsWith('CASCADING') || row[0]?.startsWith('PASSAGES') || row[0]?.startsWith('AVAILABLE')) {
          excelRow.font = { bold: true, size: 12 };
        }
      });
      instructionsSheet.getColumn(1).width = 80;

      // Reorder sheets
      const sheetsOrder = ['Instructions', 'Questions Template', 'Passages', 'Reference_Data', 'Lists'];
      workbook.eachSheet((sheet) => {
        const index = sheetsOrder.indexOf(sheet.name);
        if (index !== -1) sheet.orderNo = index;
      });

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Full_Length_Exam_Template.xlsx';
      link.click();
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Template Downloaded!',
        html: `
          <p>The template has <strong>cascading dropdowns</strong>:</p>
          <ul style="text-align: left;">
            <li><strong>Subject</strong> → Select first (${subjectDisplayNames.length} options)</li>
            <li><strong>Unit</strong> → Auto-filters by Subject!</li>
            <li><strong>Topic</strong> → Auto-filters by Unit!</li>
            <li><strong>Passage Title & Text</strong> → Optional, for reading comprehension</li>
            <li><strong>Correct Answer</strong> → ${ANSWER_DROPDOWN_STRING}</li>
          </ul>
          <p><strong>20 rows pre-filled</strong> with defaults. Add more as needed.</p>
        `,
        confirmButtonText: 'Got it!',
        confirmButtonColor: '#5e72e4'
      });
    } catch (error) {
      console.error('Error generating template:', error);
      Swal.fire('Error', 'Failed to generate template. Please try again.', 'error');
    }
  };

  // Parse uploaded file
  const parseFile = useCallback((uploadedFile, zipImages = {}) => {
    setIsParsing(true);
    setParseErrors([]);
    setParsedQuestions([]);
    setEditingIndex(null);
    setCurrentPage(1);
    setEditorStates({});

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Parse Passages sheet first (if present)
        const passageMap = {};
        const passagesSheetName = workbook.SheetNames.find(name =>
          name.toLowerCase() === 'passages'
        );
        if (passagesSheetName) {
          const passagesWs = workbook.Sheets[passagesSheetName];
          const passagesData = XLSX.utils.sheet_to_json(passagesWs, { header: 1 });
          let passageIdx = 0;
          for (let i = 1; i < passagesData.length; i++) {
            const pRow = passagesData[i];
            const pTitle = pRow?.[0]?.toString().trim() || '';
            const pText = pRow?.[1]?.toString().trim() || '';
            if (pTitle && pText) {
              passageIdx++;
              passageMap[pTitle] = {
                passageId: `P-${String(passageIdx).padStart(3, '0')}`,
                title: pTitle,
                passageText: textToDraftRaw(pText),
                _plainPassageText: pText
              };
            }
          }
        }

        // Find the Questions sheet
        let sheetName = workbook.SheetNames.find(name =>
          name.toLowerCase().includes('questions') || name.toLowerCase().includes('template')
        );
        if (!sheetName) {
          sheetName = workbook.SheetNames.find(name =>
            !['instructions', 'reference_data', 'lists', 'passages'].includes(name.toLowerCase())
          );
        }
        if (!sheetName) sheetName = workbook.SheetNames[0];

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setParseErrors([{ row: 0, message: 'File appears to be empty or has no data rows.' }]);
          setIsParsing(false);
          return;
        }

        // Skip header row
        const errors = [];
        const questions = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0 || row.every(cell => !cell || cell.toString().trim() === '')) {
            continue; // Skip empty rows
          }

          const rowNum = i + 1;
          const rowErrors = [];

          const subject = row[0]?.toString().trim() || '';
          const unit = row[1]?.toString().trim() || '';
          const topic = row[2]?.toString().trim() || '';
          const passageTitle = row[3]?.toString().trim() || '';
          const questionText = row[4]?.toString().trim() || '';
          // Read choices dynamically
          const choiceTexts = CHOICE_LABELS.map((_, ci) => row[FIRST_CHOICE_COL + ci]?.toString().trim() || '');
          const correctAnswer = row[FIRST_CHOICE_COL + MAX_CHOICES]?.toString().trim().toUpperCase() || '';
          const explanation = row[FIRST_CHOICE_COL + MAX_CHOICES + 1]?.toString().trim() || '';

          // Read image filenames and widths (optional columns, alternating: image, width, image, width, ...)
          const questionImageFilename = row[QUESTION_IMAGE_COL]?.toString().trim() || '';
          const questionImageWidth = row[QUESTION_IMAGE_WIDTH_COL]?.toString().trim() || '250';
          const choiceImageFilenames = CHOICE_LABELS.map((_, ci) => row[FIRST_CHOICE_IMAGE_COL + ci * 2]?.toString().trim() || '');
          const choiceImageWidths = CHOICE_LABELS.map((_, ci) => row[FIRST_CHOICE_IMAGE_COL + ci * 2 + 1]?.toString().trim() || '150');
          const explanationImageFilename = row[EXPLANATION_IMAGE_COL]?.toString().trim() || '';
          const explanationImageWidth = row[EXPLANATION_IMAGE_WIDTH_COL]?.toString().trim() || '250';

          // Validate required fields
          if (!subject) rowErrors.push('Subject is required');
          if (!questionText) rowErrors.push('Question Text is required');
          if (!choiceTexts[0] && !choiceImageFilenames[0]) rowErrors.push('Choice A is required (text or image)');
          if (!choiceTexts[1] && !choiceImageFilenames[1]) rowErrors.push('Choice B is required (text or image)');
          if (!correctAnswer) {
            rowErrors.push('Correct Answer is required');
          } else if (!CHOICE_LABELS.includes(correctAnswer)) {
            rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
          } else {
            // Validate correct answer references a non-empty choice (text or image)
            const correctIdx = CHOICE_LABELS.indexOf(correctAnswer);
            if (!choiceTexts[correctIdx] && !choiceImageFilenames[correctIdx]) {
              rowErrors.push(`Correct Answer is ${correctAnswer} but Choice ${correctAnswer} is empty`);
            }
          }

          // Create blob URLs for image preview
          let questionImageBlobUrl = null;
          if (questionImageFilename && zipImages[questionImageFilename.toLowerCase()]) {
            questionImageBlobUrl = URL.createObjectURL(zipImages[questionImageFilename.toLowerCase()]);
          }
          const choiceImageBlobUrls = choiceImageFilenames.map(fn => {
            if (fn && zipImages[fn.toLowerCase()]) return URL.createObjectURL(zipImages[fn.toLowerCase()]);
            return null;
          });
          let explanationImageBlobUrl = null;
          if (explanationImageFilename && zipImages[explanationImageFilename.toLowerCase()]) {
            explanationImageBlobUrl = URL.createObjectURL(zipImages[explanationImageFilename.toLowerCase()]);
          }

          // Build choices array (include choices with text and/or image)
          const choices = [];
          choiceTexts.forEach((text, ci) => {
            if (text || choiceImageFilenames[ci]) {
              const choiceBlobUrl = choiceImageBlobUrls[ci];
              choices.push({ choiceId: ci.toString(), text: choiceBlobUrl ? textToDraftRawWithImage(text, choiceBlobUrl, choiceImageWidths[ci]) : textToDraftRaw(text || ' '), label: CHOICE_LABELS[ci] });
            }
          });

          // Resolve subject/unit/topic to database IDs
          const matchedSubject = resolveSubject(subject, subjects);
          const matchedUnit = resolveUnit(unit, units, matchedSubject?.subjectId);
          const matchedTopic = topic ? resolveTopic(topic, topics, matchedUnit?.unitId) : null;

          // Validate passage title against Passages sheet
          if (passageTitle && !passageMap[passageTitle]) {
            rowErrors.push(`Passage "${passageTitle}" not found in the Passages sheet. Please add it there first.`);
          }

          const question = {
            rowNumber: rowNum,
            subjectName: subject,
            unitName: unit,
            topicName: topic,
            passageTitle,
            passageId: passageMap[passageTitle]?.passageId || null,
            subjectId: matchedSubject?.subjectId || null,
            unitId: matchedUnit?.unitId || null,
            topicId: matchedTopic?.topicId || null,
            questionText: questionImageBlobUrl ? textToDraftRawWithImage(questionText, questionImageBlobUrl, questionImageWidth) : textToDraftRaw(questionText),
            choices,
            correctAnswer: correctAnswer ? [CORRECT_ANSWER_MAP[correctAnswer]] : [],
            correctAnswerLabel: correctAnswer,
            explanationText: explanationImageBlobUrl ? textToDraftRawWithImage(explanation, explanationImageBlobUrl, explanationImageWidth) : textToDraftRaw(explanation),
            // Keep plain text versions for editing
            _plainQuestionText: questionText,
            _plainChoices: choiceTexts,
            _plainExplanation: explanation,
            questionImageBlobUrl,
            choiceImageBlobUrls,
            explanationImageBlobUrl,
            questionImageFilename,
            choiceImageFilenames,
            explanationImageFilename,
            questionImageWidth,
            choiceImageWidths,
            explanationImageWidth,
            hasErrors: rowErrors.length > 0,
            errors: rowErrors
          };

          questions.push(question);

          if (rowErrors.length > 0) {
            errors.push({ row: rowNum, message: rowErrors.join('; ') });
          }
        }

        // Build passages array from the parsed Passages sheet
        setParsedPassages(Object.values(passageMap));
        setParsedQuestions(questions);
        setParseErrors(errors);

        if (questions.length === 0) {
          setParseErrors([{ row: 0, message: 'No valid question rows found in the file.' }]);
        }
      } catch (err) {
        console.error('Parse error:', err);
        setParseErrors([{ row: 0, message: `Failed to parse file: ${err.message}` }]);
      }

      setIsParsing(false);
    };

    reader.readAsArrayBuffer(uploadedFile);
  }, [subjects, units, topics]);

  // Handle file drop/select
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (ext === 'zip') {
        try {
          const { excelFile, images } = await extractZip(selectedFile);
          setImageFiles(images);
          parseFile(excelFile, images);
        } catch (err) {
          setParseErrors([{ row: 0, message: err.message }]);
        }
      } else {
        setImageFiles({});
        parseFile(selectedFile);
      }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      const ext = droppedFile.name.split('.').pop().toLowerCase();
      if (ext === 'zip') {
        try {
          const { excelFile, images } = await extractZip(droppedFile);
          setImageFiles(images);
          parseFile(excelFile, images);
        } catch (err) {
          setParseErrors([{ row: 0, message: err.message }]);
        }
      } else {
        setImageFiles({});
        parseFile(droppedFile);
      }
    }
  };

  // Update a question field during review/edit
  const updateQuestion = (index, field, value) => {
    setParsedQuestions(prev => {
      const updated = [...prev];
      const q = { ...updated[index] };

      if (field === 'subjectName') {
        q.subjectName = value;
      } else if (field === 'unitName') {
        q.unitName = value;
      } else if (field === 'topicName') {
        q.topicName = value;
      } else if (field === 'questionText') {
        q._plainQuestionText = value;
        q.questionText = textToDraftRaw(value);
      } else if (field.startsWith('choice') && field.length === 7) {
        // Handle choiceA, choiceB, ... choiceJ
        const choiceLetter = field.replace('choice', '');
        const choiceIdx = CHOICE_LABELS.indexOf(choiceLetter);
        if (choiceIdx >= 0) {
          if (!q._plainChoices) q._plainChoices = [];
          while (q._plainChoices.length <= choiceIdx) q._plainChoices.push('');
          q._plainChoices[choiceIdx] = value;
          const choiceIdStr = choiceIdx.toString();
          if (value) {
            const existing = (q.choices || []).find(c => c.choiceId === choiceIdStr);
            if (existing) {
              q.choices = q.choices.map(c => c.choiceId === choiceIdStr ? { ...c, text: textToDraftRaw(value) } : c);
            } else {
              q.choices = [...(q.choices || []), { choiceId: choiceIdStr, text: textToDraftRaw(value), label: CHOICE_LABELS[choiceIdx] }];
              q.choices.sort((a, b) => a.choiceId.localeCompare(b.choiceId));
            }
          } else if (choiceIdx >= 2) {
            q.choices = (q.choices || []).filter(c => c.choiceId !== choiceIdStr);
          }
        }
      } else if (field === 'correctAnswer') {
        q.correctAnswerLabel = value;
        q.correctAnswer = value ? [CORRECT_ANSWER_MAP[value]] : [];
      } else if (field === 'explanation') {
        q._plainExplanation = value;
        q.explanationText = textToDraftRaw(value);
      }

      // Re-validate
      const rowErrors = [];
      if (!q.subjectName) rowErrors.push('Subject is required');
      if (!q._plainQuestionText) rowErrors.push('Question Text is required');
      if (!choiceHasContent(q, 0)) rowErrors.push('Choice A is required');
      if (!choiceHasContent(q, 1)) rowErrors.push('Choice B is required');
      if (!q.correctAnswerLabel) {
        rowErrors.push('Correct Answer is required');
      } else if (!CHOICE_LABELS.includes(q.correctAnswerLabel)) {
        rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
      } else {
        const correctIdx = CHOICE_LABELS.indexOf(q.correctAnswerLabel);
        if (!choiceHasContent(q, correctIdx)) rowErrors.push(`Correct Answer is ${q.correctAnswerLabel} but Choice ${q.correctAnswerLabel} is empty`);
      }
      q.hasErrors = rowErrors.length > 0;
      q.errors = rowErrors;

      updated[index] = q;
      return updated;
    });
  };

  // Delete a question from the list
  const deleteQuestion = (index) => {
    setParsedQuestions(prev => prev.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
    // Update parse errors
    setParseErrors(prev => prev.filter(e => e.row !== parsedQuestions[index]?.rowNumber));
  };

  // Filtered questions for search
  const displayedQuestions = searchTerm
    ? parsedQuestions.filter((q, i) => {
        const text = (q._plainQuestionText || '').toLowerCase();
        const subject = (q.subjectName || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        return text.includes(term) || subject.includes(term) || String(i + 1).includes(term);
      })
    : parsedQuestions;

  // Pagination
  const totalPages = Math.ceil(displayedQuestions.length / questionsPerPage);
  const paginatedQuestions = displayedQuestions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const errorCount = parsedQuestions.filter(q => q.hasErrors).length;
  const validCount = parsedQuestions.length - errorCount;
  const subjectCounts = parsedQuestions.reduce((acc, q) => {
    if (q.subjectName) acc[q.subjectName] = (acc[q.subjectName] || 0) + 1;
    return acc;
  }, {});

  // Section helpers
  const sectionQuestionTotal = sectionConfig.reduce((sum, s) => sum + (parseInt(s.questionCount) || 0), 0);
  const sectionCountMismatch = enableSections && parsedQuestions.length > 0 && sectionQuestionTotal !== parsedQuestions.length;

  const updateSectionField = (index, field, value) => {
    setSectionConfig(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addSection = () => {
    setSectionConfig(prev => [
      ...prev,
      { name: `Section ${prev.length + 1}`, questionCount: 0, timeLimitMinutes: 180 }
    ]);
  };

  const removeSection = (index) => {
    if (sectionConfig.length <= 2) return;
    setSectionConfig(prev => prev.filter((_, i) => i !== index));
  };

  const autoDistributeQuestions = () => {
    if (parsedQuestions.length === 0) return;
    const count = sectionConfig.length;
    const base = Math.floor(parsedQuestions.length / count);
    const remainder = parsedQuestions.length % count;
    setSectionConfig(prev => prev.map((s, i) => ({
      ...s,
      questionCount: base + (i < remainder ? 1 : 0)
    })));
  };

  // Handle form submission
  const handleCreateExam = async () => {
    if (!examName.trim()) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Please enter an exam name.', confirmButtonColor: '#5e72e4' });
      return;
    }
    if (parsedQuestions.length === 0) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Please upload an Excel file with questions.', confirmButtonColor: '#5e72e4' });
      return;
    }
    if (errorCount > 0) {
      Swal.fire({ icon: 'error', title: 'Error', text: `Please fix ${errorCount} question(s) with errors before creating the exam.`, confirmButtonColor: '#5e72e4' });
      return;
    }

    // Validate sections if enabled
    if (enableSections) {
      if (sectionCountMismatch) {
        Swal.fire({ icon: 'error', title: 'Error', text: `Section question counts (${sectionQuestionTotal}) must equal total questions (${parsedQuestions.length}).`, confirmButtonColor: '#5e72e4' });
        return;
      }
      const invalidSection = sectionConfig.find(s => !s.name.trim() || (parseInt(s.questionCount) || 0) <= 0 || (parseInt(s.timeLimitMinutes) || 0) <= 0);
      if (invalidSection) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Each section must have a name, at least 1 question, and a time limit greater than 0.', confirmButtonColor: '#5e72e4' });
        return;
      }
    }

    // Upload images from ZIP if any
    const imageUrlMap = {};
    const allFilenames = new Set();
    parsedQuestions.forEach(q => {
      if (q.questionImageFilename) allFilenames.add(q.questionImageFilename.toLowerCase());
      (q.choiceImageFilenames || []).forEach(fn => { if (fn) allFilenames.add(fn.toLowerCase()); });
      if (q.explanationImageFilename) allFilenames.add(q.explanationImageFilename.toLowerCase());
    });

    if (allFilenames.size > 0 && Object.keys(imageFiles).length > 0) {
      setIsUploadingImages(true);
      setImageUploadProgress(0);
      const fns = Array.from(allFilenames);
      for (let i = 0; i < fns.length; i++) {
        const fn = fns[i];
        const imgFile = imageFiles[fn];
        if (imgFile) {
          try {
            const optimized = await optimizeImage(imgFile, { maxW: 800, quality: 0.8 });
            const url = await uploadImage(optimized);
            imageUrlMap[fn] = url;
          } catch (err) {
            console.error(`Failed to upload image ${fn}:`, err);
          }
        }
        setImageUploadProgress(Math.round(((i + 1) / fns.length) * 100));
      }
      setIsUploadingImages(false);
    }

    // Strip internal fields before sending to action
    const questions = parsedQuestions.map(q => {
      const qImgUrl = imageUrlMap[q.questionImageFilename?.toLowerCase()] || null;
      const expImgUrl = imageUrlMap[q.explanationImageFilename?.toLowerCase()] || null;
      return {
        subjectName: q.subjectName,
        unitName: q.unitName || '',
        topicName: q.topicName || '',
        subjectId: q.subjectId || null,
        unitId: q.unitId || null,
        topicId: q.topicId || null,
        questionText: finalizeRawContent(q.questionText, q.questionImageBlobUrl, qImgUrl, q.questionImageWidth),
        choices: q.choices.map(c => {
          const choiceIdx = parseInt(c.choiceId);
          const cImgUrl = imageUrlMap[(q.choiceImageFilenames || [])[choiceIdx]?.toLowerCase()] || null;
          const cBlobUrl = (q.choiceImageBlobUrls || [])[choiceIdx] || null;
          return { choiceId: c.choiceId, text: finalizeRawContent(c.text, cBlobUrl, cImgUrl, (q.choiceImageWidths || [])[choiceIdx]) };
        }),
        correctAnswer: q.correctAnswer,
        explanationText: finalizeRawContent(q.explanationText, q.explanationImageBlobUrl, expImgUrl, q.explanationImageWidth),
        ...(q.passageId ? { passageId: q.passageId, passageTitle: q.passageTitle } : {}),
      };
    });

    // Build passages array (title + text, stored separately on the exam document)
    const passages = parsedPassages.map(p => ({
      passageId: p.passageId,
      title: p.title,
      passageText: p.passageText
    }));

    const examPayload = {
      name: examName.trim(),
      questions,
      isActive,
      ...(passages.length > 0 ? { passages } : {}),
    };

    if (enableSections) {
      examPayload.sections = sectionConfig.map((s, idx) => ({
        sectionIndex: idx,
        name: s.name,
        questionCount: parseInt(s.questionCount) || 0,
        timeLimitSeconds: (parseInt(s.timeLimitMinutes) || 0) * 60,
      }));
      examPayload.breakDurationSeconds = (parseInt(breakDurationMinutes) || 0) * 60;
    }

    const result = await dispatch(createFullLengthExam(examPayload));

    if (result.success) {
      history.push('/admin/question-bank/full-length-exams');
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header bg-gradient-success pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center">
              <Col>
                <h1 className="text-white mb-0">Create Full-Length Exam</h1>
                <p className="text-white mt-0 mb-0">Upload an Excel file to create a full-length exam</p>
              </Col>
              <Col className="text-right">
                <Button
                  color="primary"
                  onClick={() => history.push('/admin/question-bank/full-length-exams')}
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Exams
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <Col lg="8">
            {/* Exam Details Card */}
            <Card className="shadow mb-4">
              <CardHeader>
                <h3 className="mb-0">Exam Details</h3>
              </CardHeader>
              <CardBody>
                <Form>
                  <FormGroup>
                    <Label for="examName">Exam Name *</Label>
                    <Input
                      type="text"
                      id="examName"
                      placeholder="e.g., Full-Length Practice Exam 1"
                      value={examName}
                      onChange={(e) => setExamName(e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <CustomInput
                      type="checkbox"
                      id="isActive"
                      label="Make exam available to students immediately"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>

            {/* Section Configuration Card */}
            <Card className="shadow mb-4">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Section Configuration</h3>
                <CustomInput
                  type="switch"
                  id="enableSections"
                  label="Divide into sections"
                  checked={enableSections}
                  onChange={(e) => setEnableSections(e.target.checked)}
                />
              </CardHeader>
              {enableSections && (
                <CardBody>
                  <Alert color="info" className="py-2 mb-3">
                    <i className="fas fa-info-circle mr-2"></i>
                    Questions are assigned to sections sequentially based on upload order.
                    For example, if Section 1 has 10 questions and Section 2 has 10 questions,
                    the first 10 uploaded questions go to Section 1 and the next 10 go to Section 2.
                    Questions are then shuffled randomly within each section when a student starts the exam.
                  </Alert>
                  {sectionConfig.map((section, idx) => (
                    <div key={idx} className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fe' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong style={{ fontSize: '0.9rem' }}>Section {idx + 1}</strong>
                        {sectionConfig.length > 2 && (
                          <Button color="link" size="sm" className="text-danger p-0" onClick={() => removeSection(idx)}>
                            <i className="fas fa-times"></i>
                          </Button>
                        )}
                      </div>
                      <Row>
                        <Col md="4">
                          <FormGroup className="mb-0">
                            <Label className="form-control-label" style={{ fontSize: '0.8rem' }}>Name</Label>
                            <Input
                              bsSize="sm"
                              value={section.name}
                              onChange={(e) => updateSectionField(idx, 'name', e.target.value)}
                              placeholder="Section name"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup className="mb-0">
                            <Label className="form-control-label" style={{ fontSize: '0.8rem' }}>Questions</Label>
                            <Input
                              bsSize="sm"
                              type="number"
                              min="1"
                              value={section.questionCount || ''}
                              onChange={(e) => updateSectionField(idx, 'questionCount', e.target.value)}
                              placeholder="# of questions"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup className="mb-0">
                            <Label className="form-control-label" style={{ fontSize: '0.8rem' }}>Time Limit (min)</Label>
                            <Input
                              bsSize="sm"
                              type="number"
                              min="1"
                              value={section.timeLimitMinutes || ''}
                              onChange={(e) => updateSectionField(idx, 'timeLimitMinutes', e.target.value)}
                              placeholder="Minutes"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                  ))}

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button color="outline-primary" size="sm" onClick={addSection}>
                      <i className="fas fa-plus mr-1"></i> Add Section
                    </Button>
                    {parsedQuestions.length > 0 && (
                      <Button color="outline-info" size="sm" onClick={autoDistributeQuestions}>
                        <i className="fas fa-magic mr-1"></i> Auto-Distribute
                      </Button>
                    )}
                  </div>

                  {/* Break Duration */}
                  <div className="border rounded p-3" style={{ backgroundColor: '#fff8e1' }}>
                    <Row className="align-items-center">
                      <Col md="6">
                        <FormGroup className="mb-0">
                          <Label className="form-control-label" style={{ fontSize: '0.8rem' }}>
                            <i className="fas fa-coffee mr-1"></i> Break Between Sections (min)
                          </Label>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            value={breakDurationMinutes}
                            onChange={(e) => setBreakDurationMinutes(e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <small className="text-muted">Students can skip the break and start the next section immediately.</small>
                      </Col>
                    </Row>
                  </div>

                  {/* Validation message */}
                  {parsedQuestions.length > 0 && (
                    <div className="mt-3">
                      {sectionCountMismatch ? (
                        <Alert color="danger" className="mb-0 py-2">
                          <i className="fas fa-exclamation-triangle mr-1"></i>
                          Section total ({sectionQuestionTotal}) does not match uploaded questions ({parsedQuestions.length}).
                          {' '}Difference: {Math.abs(sectionQuestionTotal - parsedQuestions.length)}
                        </Alert>
                      ) : sectionQuestionTotal > 0 && (
                        <Alert color="success" className="mb-0 py-2">
                          <i className="fas fa-check-circle mr-1"></i>
                          Sections correctly total {parsedQuestions.length} questions.
                        </Alert>
                      )}
                    </div>
                  )}
                </CardBody>
              )}
            </Card>

            {/* Upload Card */}
            <Card className="shadow mb-4">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Upload Questions</h3>
                <Button color="info" size="sm" onClick={downloadTemplate}>
                  <i className="fas fa-download mr-2"></i>
                  Download Template
                </Button>
              </CardHeader>
              <CardBody>
                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => document.getElementById('file-upload').click()}
                  style={{
                    border: '2px dashed #dee2e6',
                    borderRadius: '8px',
                    padding: '40px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#f8f9fe',
                    transition: 'border-color 0.2s'
                  }}
                >
                  <input
                    type="file"
                    id="file-upload"
                    accept=".xlsx,.xls,.csv,.zip"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3" style={{ display: 'block' }}></i>
                  {file ? (
                    <div>
                      <p className="mb-1 font-weight-bold">{file.name}</p>
                      <small className="text-muted">Click or drop a new file to replace</small>
                    </div>
                  ) : (
                    <div>
                      <p className="mb-1">Drag & drop your Excel or ZIP file here, or click to browse</p>
                      <small className="text-muted">Supports .xlsx, .xls, .csv, .zip (for images)</small>
                    </div>
                  )}
                </div>

                {isParsing && (
                  <div className="text-center mt-3">
                    <Spinner size="sm" color="primary" className="mr-2" />
                    Parsing file...
                  </div>
                )}

                {isUploadingImages && (
                  <div className="mt-3">
                    <div className="d-flex align-items-center mb-2">
                      <Spinner size="sm" color="warning" className="mr-2" />
                      <span>Uploading images... {imageUploadProgress}%</span>
                    </div>
                    <Progress value={imageUploadProgress} color="warning" />
                  </div>
                )}

                {/* Parse errors */}
                {parseErrors.length > 0 && parsedQuestions.length === 0 && (
                  <Alert color="danger" className="mt-3">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    {parseErrors[0].message}
                  </Alert>
                )}
              </CardBody>
            </Card>

            {/* Review Questions Card */}
            {parsedQuestions.length > 0 && (
              <Card className="shadow mb-4">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-0 d-inline">Review Questions</h3>
                    <Badge color={errorCount > 0 ? 'danger' : 'success'} className="ml-2">
                      {validCount} valid
                    </Badge>
                    {errorCount > 0 && (
                      <Badge color="danger" className="ml-1">{errorCount} errors</Badge>
                    )}
                  </div>
                  <Input
                    type="text"
                    placeholder="Search questions..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    style={{ maxWidth: '250px' }}
                  />
                </CardHeader>
                <CardBody className="p-0">
                  <style>{editorContainerStyles}</style>
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table hover responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          <th style={{ width: '120px' }}>Subject</th>
                          <th style={{ width: '120px' }}>Unit</th>
                          <th>Question</th>
                          <th style={{ width: '50px' }}>Ans</th>
                          <th style={{ width: '70px' }}>Status</th>
                          <th style={{ width: '80px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedQuestions.map((question) => {
                          const realIndex = parsedQuestions.indexOf(question);
                          const isEditing = editingIndex === realIndex;

                          return (
                            <React.Fragment key={realIndex}>
                              <tr
                                style={{
                                  backgroundColor: question.hasErrors ? '#fff5f5' : undefined,
                                  cursor: 'pointer'
                                }}
                                onClick={() => setEditingIndex(isEditing ? null : realIndex)}
                              >
                                <td className="font-weight-bold">{realIndex + 1}</td>
                                <td>
                                  <small>{question.subjectName || <span className="text-danger">Missing</span>}</small>
                                </td>
                                <td>
                                  <small className="text-muted">{question.unitName || '-'}</small>
                                </td>
                                <td>
                                  {question.passageTitle && (
                                    <Badge color="success" className="mr-1" style={{ fontSize: '0.65rem' }}>
                                      <i className="fas fa-file-alt mr-1"></i>{question.passageTitle}
                                    </Badge>
                                  )}
                                  <small className="text-truncate d-block" style={{ maxWidth: '300px' }}>
                                    {question._plainQuestionText
                                      ? (question._plainQuestionText.length > 80
                                          ? question._plainQuestionText.substring(0, 80) + '...'
                                          : question._plainQuestionText)
                                      : <span className="text-danger">Missing</span>}
                                  </small>
                                </td>
                                <td>
                                  <Badge color={question.correctAnswerLabel ? 'success' : 'danger'} pill>
                                    {question.correctAnswerLabel || '?'}
                                  </Badge>
                                </td>
                                <td>
                                  {question.hasErrors ? (
                                    <Badge color="danger" pill>Error</Badge>
                                  ) : (
                                    <Badge color="success" pill>Valid</Badge>
                                  )}
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 mr-2"
                                    onClick={() => setEditingIndex(isEditing ? null : realIndex)}
                                    title="Edit"
                                  >
                                    <i className={`fas fa-${isEditing ? 'chevron-up' : 'edit'}`}></i>
                                  </Button>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 text-danger"
                                    onClick={() => deleteQuestion(realIndex)}
                                    title="Remove"
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </Button>
                                </td>
                              </tr>

                              {/* Inline edit panel */}
                              <tr style={{ display: isEditing ? 'table-row' : 'none' }}>
                                <td colSpan="7" className="p-3 bg-lighter">
                                  {question.hasErrors && (
                                    <Alert color="danger" className="py-2 mb-3">
                                      <small><i className="fas fa-exclamation-circle mr-1"></i>{question.errors.join(' | ')}</small>
                                    </Alert>
                                  )}
                                  <Row>
                                    <Col md="3">
                                      <FormGroup>
                                        <Label className="small font-weight-bold">Subject *</Label>
                                        <Input
                                          bsSize="sm"
                                          value={question.subjectName || ''}
                                          onChange={(e) => updateQuestion(realIndex, 'subjectName', e.target.value)}
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col md="3">
                                      <FormGroup>
                                        <Label className="small">Unit</Label>
                                        <Input
                                          bsSize="sm"
                                          value={question.unitName || ''}
                                          onChange={(e) => updateQuestion(realIndex, 'unitName', e.target.value)}
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col md="3">
                                      <FormGroup>
                                        <Label className="small">Topic</Label>
                                        <Input
                                          bsSize="sm"
                                          value={question.topicName || ''}
                                          onChange={(e) => updateQuestion(realIndex, 'topicName', e.target.value)}
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col md="3">
                                      <FormGroup>
                                        <Label className="small font-weight-bold">Correct Answer *</Label>
                                        <Input
                                          bsSize="sm"
                                          type="select"
                                          value={question.correctAnswerLabel || ''}
                                          onChange={(e) => updateQuestion(realIndex, 'correctAnswer', e.target.value)}
                                        >
                                          <option value="">Select...</option>
                                          {CHOICE_LABELS.map(label => (
                                            <option key={label} value={label}>{label}</option>
                                          ))}
                                        </Input>
                                      </FormGroup>
                                    </Col>
                                  </Row>
                                  <FormGroup>
                                    <Label className="small font-weight-bold d-flex align-items-center">
                                      Question Text *
                                    </Label>
                                    {editorStates[realIndex] && (
                                      <div className="border rounded p-2 question-editor-container">
                                        <Editor
                                          editorState={editorStates[realIndex].questionText}
                                          onEditorStateChange={(state) => handleEditorChange(realIndex, 'questionText', state)}
                                          toolbar={questionTextToolbar}
                                          customBlockRenderFunc={createBlockRenderer(realIndex, 'questionText')}
                                        />
                                      </div>
                                    )}
                                  </FormGroup>
                                  {editorStates[realIndex] && (
                                    <>
                                      <FormGroup>
                                        <Label className="small font-weight-bold">Answer Choices * (Select the correct answer)</Label>
                                        {Array.from({ length: Math.max(question.choices?.length || 0, 2) }, (_, choiceIdx) => {
                                          const choiceField = `choice${CHOICE_LABELS[choiceIdx]}`;
                                          return (
                                            <div key={choiceIdx} className="mb-3 d-flex align-items-start">
                                              <div className="d-flex align-items-center mr-2" style={{ minWidth: '50px', paddingTop: '10px' }}>
                                                <div className="custom-control custom-radio">
                                                  <input
                                                    type="radio"
                                                    className="custom-control-input"
                                                    id={`fle-choice-${realIndex}-${choiceIdx}`}
                                                    name={`fle-correctAnswer-${realIndex}`}
                                                    checked={question.correctAnswerLabel === CHOICE_LABELS[choiceIdx]}
                                                    onChange={() => updateQuestion(realIndex, 'correctAnswer', CHOICE_LABELS[choiceIdx])}
                                                  />
                                                  <label className="custom-control-label" htmlFor={`fle-choice-${realIndex}-${choiceIdx}`}></label>
                                                </div>
                                                <span className="ml-2 font-weight-bold text-muted">
                                                  {CHOICE_LABELS[choiceIdx]}.
                                                  {choiceIdx < 2 && <span className="text-danger">*</span>}
                                                </span>
                                              </div>
                                              <div className="flex-grow-1">
                                                {editorStates[realIndex]?.[choiceField] && (
                                                  <div className="border rounded p-2 choice-editor-container">
                                                    <Editor
                                                      editorState={editorStates[realIndex][choiceField]}
                                                      onEditorStateChange={(state) => handleEditorChange(realIndex, choiceField, state)}
                                                      toolbar={choiceToolbar}
                                                      customBlockRenderFunc={createBlockRenderer(realIndex, choiceField)}
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </FormGroup>
                                      <FormGroup>
                                        <Label className="small d-flex align-items-center">
                                          Explanation
                                        </Label>
                                        <div className="border rounded p-2 explanation-editor-container">
                                          <Editor
                                            editorState={editorStates[realIndex].explanation}
                                            onEditorStateChange={(state) => handleEditorChange(realIndex, 'explanation', state)}
                                            toolbar={explanationToolbar}
                                            customBlockRenderFunc={createBlockRenderer(realIndex, 'explanation')}
                                          />
                                        </div>
                                      </FormGroup>
                                    </>
                                  )}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                      <small className="text-muted">
                        Showing {((currentPage - 1) * questionsPerPage) + 1}-{Math.min(currentPage * questionsPerPage, displayedQuestions.length)} of {displayedQuestions.length}
                      </small>
                      <div>
                        <Button size="sm" color="secondary" outline className="mr-1" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                          <i className="fas fa-angle-double-left"></i>
                        </Button>
                        <Button size="sm" color="secondary" outline className="mr-1" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                          <i className="fas fa-angle-left"></i>
                        </Button>
                        <span className="mx-2 small">Page {currentPage} of {totalPages}</span>
                        <Button size="sm" color="secondary" outline className="mr-1" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                          <i className="fas fa-angle-right"></i>
                        </Button>
                        <Button size="sm" color="secondary" outline disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                          <i className="fas fa-angle-double-right"></i>
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </Col>

          {/* Summary Card */}
          <Col lg="4" className="mt-4 mt-lg-0">
            <Card className="shadow" style={{ position: 'sticky', top: '20px' }}>
              <CardHeader>
                <h3 className="mb-0">Exam Summary</h3>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <strong>Name:</strong>
                  <p className="mb-0">{examName || <span className="text-muted">Not set</span>}</p>
                </div>
                <div className="mb-3">
                  <strong>Questions:</strong>
                  <p className="mb-0">
                    {parsedQuestions.length > 0 ? (
                      <>
                        {parsedQuestions.length} uploaded
                        {errorCount > 0 && <span className="text-danger"> ({errorCount} with errors)</span>}
                      </>
                    ) : (
                      <span className="text-muted">No file uploaded</span>
                    )}
                  </p>
                </div>
                {parsedQuestions.length > 0 && (
                  <div className="mb-3">
                    <strong>Time Limit:</strong>
                    {enableSections ? (
                      <div className="mt-1">
                        {sectionConfig.map((s, idx) => (
                          <div key={idx} className="d-flex justify-content-between mb-1">
                            <small>{s.name || `Section ${idx + 1}`}</small>
                            <small>{parseInt(s.questionCount) || 0}q, {formatTime((parseInt(s.timeLimitMinutes) || 0) * 60)}</small>
                          </div>
                        ))}
                        {parseInt(breakDurationMinutes) > 0 && (
                          <div className="d-flex justify-content-between mb-1">
                            <small className="text-muted">Break</small>
                            <small className="text-muted">{breakDurationMinutes}m</small>
                          </div>
                        )}
                        <hr className="my-1" />
                        <div className="d-flex justify-content-between">
                          <small><strong>Total</strong></small>
                          <small><strong>{formatTime(
                            sectionConfig.reduce((sum, s) => sum + (parseInt(s.timeLimitMinutes) || 0) * 60, 0)
                            + (parseInt(breakDurationMinutes) || 0) * 60 * (sectionConfig.length - 1)
                          )}</strong></small>
                        </div>
                      </div>
                    ) : (
                      <p className="mb-0">{formatTime(parsedQuestions.length * 66)}</p>
                    )}
                  </div>
                )}
                <div className="mb-3">
                  <strong>Status:</strong>
                  <Badge color={isActive ? 'success' : 'secondary'} className="ml-2">
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {/* Subject breakdown */}
                {Object.keys(subjectCounts).length > 0 && (
                  <div className="mb-3">
                    <strong>Subjects:</strong>
                    <div className="mt-1">
                      {Object.entries(subjectCounts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([subject, count]) => (
                          <div key={subject} className="d-flex justify-content-between mb-1">
                            <small>{subject}</small>
                            <Badge color="primary" pill>{count}</Badge>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <hr />

                <Button
                  color="success"
                  block
                  onClick={handleCreateExam}
                  disabled={createLoading || parsedQuestions.length === 0 || errorCount > 0 || sectionCountMismatch}
                >
                  {createLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i>
                      Create Exam ({parsedQuestions.length} questions)
                    </>
                  )}
                </Button>

                {errorCount > 0 && (
                  <small className="text-danger d-block mt-2 text-center">
                    Fix all errors before creating
                  </small>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CreateFullLengthExamPage;
